"use client";
import {
  BanIcon,
  OfficeBuildingIcon,
  QuestionMarkCircleIcon,
  VideoCameraIcon
} from "@heroicons/react/outline";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@cmp-src/primitives/Select";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import {
  ISchedule,
  ISlot,
  PRESENCE_STATUS_ACCEPTED,
  PRESENCE_STATUS_PENDING,
  PRESENCE_STATUS_REJECTED,
  PRESENCE_TYPE_IN_PERSON,
  PRESENCE_TYPE_REMOTE,
  PresenceStatus,
  PresenceType
} from "@gettactic/api";
import { api } from "@/lib/api/api";

export type Props = {
  schedule: ISchedule;
  userSlot: ISlot;
  remoteEnabled?: boolean;
};

function getValue(type: PresenceType | null, status: PresenceStatus) {
  if (status === PRESENCE_STATUS_ACCEPTED) {
    return type ?? PRESENCE_STATUS_ACCEPTED;
  }
  return status;
}

export function ReservationCardRespondPresence({
  schedule,
  userSlot,
  remoteEnabled
}: Props) {
  const [presenceStatus, setPresenceStatus] = useState<PresenceStatus>(
    userSlot.presence_status
  );
  const [presenceType, setPresenceType] = useState<PresenceType | null>(
    userSlot.presence_type
  );
  useEffect(() => {
    setPresenceStatus(userSlot.presence_status);
    setPresenceType(userSlot.presence_type);
  }, [userSlot]);

  const currentValue = getValue(presenceType, presenceStatus);
  const queryClient = useQueryClient();
  const onApprovalPresence = async (
    status: PresenceStatus,
    presenceType: PresenceType | null
  ) => {
    await api.client.schedules.userPresence(
      schedule.id,
      userSlot.user.id,
      status,
      presenceType ?? undefined
    );
    await queryClient.invalidateQueries({
      queryKey: ["schedules"]
    });
    await queryClient.invalidateQueries({
      queryKey: ["myAgenda"],
      exact: false
    });
    await queryClient.invalidateQueries({
      queryKey: ["tableOverview"]
    });
  };

  const options = [
    {
      value: PRESENCE_TYPE_IN_PERSON,
      label: "Office",
      icon: <OfficeBuildingIcon className="h-5 w-5 text-green-500" />
    },
    {
      value: PRESENCE_TYPE_REMOTE,
      label: "Virtual",
      icon: <VideoCameraIcon className="h-5 w-5 text-green-500" />,
      disabled: !remoteEnabled
    },
    {
      value: PRESENCE_STATUS_PENDING,
      label: "Maybe",
      icon: <QuestionMarkCircleIcon className="h-5 w-5 text-neutral-600" />
    },
    {
      value: PRESENCE_STATUS_REJECTED,
      label: "Decline",
      icon: <BanIcon className="h-5 w-5 text-red-500" />
    }
  ].filter((item) => !item.disabled);

  const handleChange = (value: string) => {
    if (value === PRESENCE_TYPE_IN_PERSON || value === PRESENCE_TYPE_REMOTE) {
      setPresenceStatus(PRESENCE_STATUS_ACCEPTED);
      setPresenceType(value);
      onApprovalPresence(PRESENCE_STATUS_ACCEPTED, value as PresenceType);
    } else {
      setPresenceStatus(value as PresenceStatus);
      setPresenceType(null);
      onApprovalPresence(value as PresenceStatus, null);
    }
  };

  return (
    <Select value={currentValue} onValueChange={handleChange}>
      <SelectTrigger
        aria-label="rsvp"
        className="h-10 w-full rounded-full border-tacticGrayBlue-400 bg-transparent text-center lg:w-[120px]"
      >
        <SelectValue placeholder="RSVP" />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            <div className="flex items-center gap-2 text-tertiary">
              {option.icon}
              <span className="text-sm">{option.label}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
